import { Payer } from "@wingspanhq/wingspan-api-sdk";

export enum ConsentFormType {
  W9 = "W9",
  W8Ben = "W8Ben",
  CanadaTaxPayerCertification = "CanadaTaxPayerCertification",
  LocalTaxInformation = "LocalTaxInformation"
}

// `Payer.countryCode` (WIN-15191) is the payee-readable payer country: an ISO
// 3166-1 alpha-2 code projected from the linked client's mirrored address. The
// backend omits the field rather than emitting an off-contract value, so an
// unlinked client — or one whose stored country is unknown — reads as US, which
// keeps such a payer on the W9/W8Ben branches instead of the Canada/Local ones.
const payerCountryOf = (payer: Payer): string => payer.countryCode || "US";

export function getConsentForms(
  payers: Payer[],
  payeeCountry: string,
  isInternationalUSPerson: boolean
): {
  consentForms: ConsentFormType[];
  taxInfoSharedPayers: {
    [keyof in ConsentFormType]: Payer[];
  };
} {
  let w9SharedPayers: Payer[] = [];
  let w8BenSharedPayers: Payer[] = [];
  let canadaTaxInfoSharedPayers: Payer[] = [];
  let localTaxInfoSharedPayers: Payer[] = [];
  const payerCountries = payers.map(payerCountryOf);
  const consentForms: ConsentFormType[] = [];
  // Refer the consent matrix for more details https://linear.app/wingspan/issue/WIN-8745#heading-afb09602

  const isUsPerson = payeeCountry === "US" || isInternationalUSPerson;

  // If payee is from US then W9 is required irrespective of payer's country
  if (isUsPerson) {
    consentForms.push(ConsentFormType.W9);
    w9SharedPayers = payers;
  }

  // If payee is non-US and payer is from US then W8Ben is required
  if (payeeCountry !== "US" && payerCountries.includes("US") && !isUsPerson) {
    consentForms.push(ConsentFormType.W8Ben);
    w8BenSharedPayers = payers.filter(payer => payerCountryOf(payer) === "US");
  }
  // CanadaTaxPayerCertification is required when:
  // 1. Payee is from Canada
  // 2. Payer is from Canada OR other country
  if (
    payeeCountry === "CA" &&
    payerCountries.some(country => country !== "US")
  ) {
    consentForms.push(ConsentFormType.CanadaTaxPayerCertification);
    canadaTaxInfoSharedPayers = payers.filter(
      payer => payerCountryOf(payer) !== "US"
    );
  }
  // LocalTaxInformation is required when:
  // 1. Payee is not from Canada and US
  // 2. Payer is not from US
  if (
    payeeCountry !== "CA" &&
    payeeCountry !== "US" &&
    payerCountries.some(country => country !== "US")
  ) {
    consentForms.push(ConsentFormType.LocalTaxInformation);
    localTaxInfoSharedPayers = payers.filter(
      payer => payerCountryOf(payer) !== "US"
    );
  }

  return {
    consentForms: consentForms.length > 0 ? consentForms : [ConsentFormType.W9],
    taxInfoSharedPayers: {
      [ConsentFormType.W9]: w9SharedPayers,
      [ConsentFormType.W8Ben]: w8BenSharedPayers,
      [ConsentFormType.CanadaTaxPayerCertification]: canadaTaxInfoSharedPayers,
      [ConsentFormType.LocalTaxInformation]: localTaxInfoSharedPayers
    }
  };
}
